import { ShowToast, Severty } from "./toast";

// Opens exactly the thing a notification is about, on the client it belongs to.
//
// Rules (they exist because of real mistakes):
//  - A post or a blog is looked up by its own id and the CLIENT is read from that record.
//    The notification's sender (`fromId`) is never used as the client: the sender is
//    usually the Brand Manager who did the action, so using it opened the BM's own
//    page instead of the client's.
//  - A blog opens in the blog editor on that client (not the Internal Review queue), a
//    post opens in the calendar drawer on that client.
//  - Anything else (notes, vault, calendar marks, LinkedIn alerts, writing assistant)
//    goes to the right tab of the client named in `data.clientId`.
//  - If the content was deleted, we land on the client's matching tab and say so.

const clientTabFor = (type, route) => {
  const byRoute = { "/calendar": "1", "/posts": "2", "/blogs": "7", "/blog": "7", "/notes": "9", "/vault": "4" };
  if (byRoute[route]) return byRoute[route];
  if (type === "post" || type === "calendar" || type === "calendar_marking" || type === "linkedin_expired") return "1";
  if (type === "blog" || type === "blogs") return "7";
  if (type === "note" || type === "notes") return "9";
  if (type === "vault") return "4";
  return null;
};

const idOf = (v) => (v && typeof v === "object" ? v._id || v.id || null : v || null);

export const openNotification = ({ item, request, navigate }) => {
  const data = item?.data || {};
  const type = String(data.type || "").toLowerCase();
  const route = String(data.route || "").toLowerCase();
  const contentId = data.id;
  const clientIdFromData = idOf(data.clientId);

  if (type === "graphics_request" && data.requestId) {
    navigate(`/graphics-requests/${data.requestId}`);
    return;
  }
  if (type === "blog_write_request" && data.requestId) {
    navigate(`/blog-write-requests/${data.requestId}`);
    return;
  }

  const goToClientTab = (clientId, extraState = {}) => {
    const tab = clientTabFor(type, route);
    navigate(`/view-clients/${clientId}`, { state: { ...(tab ? { activeTab: tab } : {}), ...extraState } });
  };

  // ── A post: open it in the client's calendar ──────────────────────────────
  if (type === "post" && contentId) {
    request({
      url: `/admin/access-module/postById/${contentId}`,
      method: "GET",
      onSuccess: (res) => {
        const post = res?.data?.post;
        const clientId = idOf(post?.userId) || clientIdFromData;
        if (!clientId) {
          ShowToast("Could not work out which client this post belongs to.", Severty.ERROR);
          return;
        }
        navigate(`/view-clients/${clientId}`, { state: { activeTab: "1", openItem: post || { _id: contentId } } });
      },
      onError: () => {
        if (clientIdFromData) {
          ShowToast("This post is no longer available.", Severty.ERROR);
          navigate(`/view-clients/${clientIdFromData}`, { state: { activeTab: "1" } });
        } else {
          ShowToast("This post is no longer available.", Severty.ERROR);
        }
      },
    });
    return;
  }

  // ── A blog: open it in the blog editor on the client it belongs to ────────
  if (type === "blog" && contentId) {
    request({
      url: `/admin/access-module/blogById/${contentId}`,
      method: "GET",
      onSuccess: (res) => {
        const blog = res?.data?.blog;
        const clientId = idOf(blog?.userId) || clientIdFromData;
        if (!blog || !clientId) {
          ShowToast("Could not open this blog.", Severty.ERROR);
          return;
        }
        navigate(`/create-new-manully-blog/${clientId}`, { state: { record: blog, isEdit: true } });
      },
      onError: () => {
        ShowToast("This blog is no longer available.", Severty.ERROR);
        if (clientIdFromData) navigate(`/view-clients/${clientIdFromData}`, { state: { activeTab: "7" } });
      },
    });
    return;
  }

  // Writing Assistant entry for a client
  if (type === "writing_assistance" && clientIdFromData) {
    navigate(`/writing-assistant/${clientIdFromData}`);
    return;
  }

  // Notes, vault, calendar marks, LinkedIn alerts ... → the matching tab of that client
  if (clientIdFromData) {
    goToClientTab(clientIdFromData);
    return;
  }

  // Real top-level pages
  if (["/internal-review", "/onboarding", "/"].includes(route)) {
    navigate(route);
    return;
  }

  // Older notifications saved before they carried the client can't be placed.
  if (contentId || route) {
    ShowToast("This older notification doesn't say which client it is for. Open the client from the client list.", Severty.INFO);
  }
};
